import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import {
  Button,
  FormGroup,
  FormControl,
} from 'react-bootstrap';

import SocialLogin from '../SocialLogin';
import Link from '../../components/Link';

import { openForgotPasswordModal, openSignupModal } from '../../actions/modalActions';

import validate from './validate';
import submit from './submit';
import messages from '../../locale/jsonMessages';

import arrow from '/public/siteImages/whiteArrow.svg';
import ShowPassword from '/public/SiteIcons/pswVisible.svg';
import HidePassword from '/public/SiteIcons/pwdHidden.svg';

import cs from '../../components/commonStyle.css';
import c from '../../components/LoginModal/LoginModal.css';
import s from './Login.css';

const LoginForm = (props) => {
  const { error, handleSubmit, submitting, siteName, openSignupModal, openForgotPasswordModal, registerURL, refer } = props;
  const [showPassword, setShowPassword] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsDisabled(false);
    }
  }, []);

  const FormControlInput = React.memo(({ input, label, type, meta: { touched, error }, className, showPassword, maxLength, formatMessage, handleChange, isRequiredInput }) => {
    return (
      <FormGroup className={cs.noMargin} id={input.name}>
        <label className='commonLabelText'><span>{label}{isRequiredInput && <sup className='isRequiredStar'>*</sup>}</span></label>
        <div className={cs.positionRelative}>
          <FormControl {...input} placeholder={label} type={showPassword === input.name ? 'text' : type} className={className} maxLength={maxLength} />
          {type === 'password' &&
            <span onClick={() => handleChange(input.name)} className='passwordShowHideIcon'>
              {showPassword === input.name ? <img src={ShowPassword} /> : <img src={HidePassword} />}
            </span>}
        </div>
        {touched && error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
      </FormGroup>
    );
  });

  const handleChange = React.useCallback((fieldName) => {
    setShowPassword(fieldName === showPassword ? "" : fieldName);
  }, [showPassword]);

  const renderFormControl = React.useCallback(({ input, label, type, meta, className, maxLength, isRequiredInput }) => {
    return (
      <FormControlInput
        input={input}
        label={label}
        type={type}
        meta={meta}
        className={className}
        showPassword={showPassword}
        maxLength={maxLength}
        formatMessage={formatMessage}
        handleChange={handleChange}
        isRequiredInput={isRequiredInput}
      />
    );
  }, [showPassword, formatMessage, handleChange]);

  return (
    <>
      <form onSubmit={handleSubmit(submit)}>
        <h3 className={cx(cs.commonTitleText, cs.fontWeightBold, cs.textAlignCenter)}>
          <FormattedMessage {...messages.dashBoardHeader} />
          <span className={cx(cs.displayBlock, cs.paddingTop1, cs.paddingBottom3)}>{siteName}</span>
        </h3>
        <label className={cx(cs.textAlignCenter, cs.displayBlock)}><FormattedMessage {...messages.loginWith} /></label>
        <SocialLogin refer={refer} />
        <strong className={c.lineThrough}><FormattedMessage {...messages.or} /></strong>
        {error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
        <div className="oneColumnGrid">
          <Field
            name="email"
            type="text"
            component={renderFormControl}
            className='commonInputField'
            label={formatMessage(messages.emailLabel)}
            maxLength={255}
            isRequiredInput
          />
          <Field
            name="password"
            type="password"
            component={renderFormControl}
            className={cx('commonInputField', 'commonRightSideIconInput')}
            label={formatMessage(messages.password)}
            showPassword={showPassword}
            maxLength={25}
            isRequiredInput
          />
          <a onClick={openForgotPasswordModal} className={cx(cs.commonMediumText, cs.siteLinkColor, cs.fontWeightMedium, cs.curserPointer, cs.paddingTop1, cs.displayinlineBlock)}>
            <FormattedMessage {...messages.forgotPassword} />
          </a>
          <Button className={cx(cs.btnPrimary, cs.spaceBottom2)} block type="submit"
            disabled={submitting || isDisabled}>
            {formatMessage(messages.login)}
            <img src={arrow} className={cx(cs.blueLeftArrow, 'loginArrowRTL')} />
          </Button>
        </div>
      </form>

      <p className={cx(cs.siteTextColor, cs.commonMediumText, cs.fontWeightNormal, cs.textAlignCenter, cs.paddingBottom1)}>
        <FormattedMessage {...messages.noAccount} />{' '}
        {registerURL ? (
          <Link className={cx(cs.siteLinkColor, cs.commonMediumText, cs.fontWeightMedium)} to={registerURL}>
            <FormattedMessage {...messages.signUp} />
          </Link>
        ) : (
          <a href="#" className={cx(cs.siteLinkColor, cs.commonMediumText, cs.fontWeightMedium)} onClick={openSignupModal}>
            <FormattedMessage {...messages.signUp} />
          </a>
        )}
      </p>
    </>
  );
};

LoginForm.propTypes = {
  openForgotPasswordModal: PropTypes.func.isRequired,
  openSignupModal: PropTypes.func.isRequired,
  siteName: PropTypes.string,
  registerURL: PropTypes.string,
  refer: PropTypes.any,
};

const mapState = state => ({
  loginModal: state.modalStatus.isLoginModalOpen,
  siteName: state.siteSettings.data.siteName,
});

const mapDispatch = {
  openForgotPasswordModal,
  openSignupModal,
};

export default reduxForm({
  form: 'LoginForm',
  validate,
  destroyOnUnmount: true,
})((withStyles(cs, c, s)(connect(mapState, mapDispatch)(LoginForm))));
